import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Row, Col, Card, Typography, Menu, Table, Button, Tag, Space, Modal, Form, Input,
  InputNumber, ColorPicker, Switch, message, Popconfirm, Tooltip, Alert,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined,
} from '@ant-design/icons';
import {
  fetchSettings, createSetting, updateSetting, deleteSetting,
} from '../services/settingsService';
import { fetchPropertyTypes } from '../services/leadService';

const { Title, Text } = Typography;

// Editable masters and their display metadata
const CATEGORIES = [
  { key: 'lead_source', label: 'Lead Sources' },
  { key: 'followup_status', label: 'Follow-up Status', color: true, requiresNext: true },
  { key: 'followup_type', label: 'Follow-up / Event Types', color: true },
  { key: 'event_type', label: 'Calendar Event Types', color: true },
  { key: 'doc_category', label: 'Document Categories' },
  { key: 'visit_type', label: 'Visit Types', color: true },
  { key: 'lost_reason', label: 'Lost Reasons' },
  { key: 'call_outcome', label: 'Call Outcomes' },
  { key: 'priority_color', label: 'Priority Colors', color: true },
  { key: 'current_situation', label: 'Current Situations' },
  { key: 'lead_tag', label: 'Lead Tags', color: true },
  { key: 'executive', label: 'Executives' },
];

const toHex = (c) => (typeof c === 'string' ? c : (c && c.toHexString ? c.toHexString() : null));

const Settings = () => {
  const [active, setActive] = useState('lead_source');
  const [data, setData] = useState({});
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const meta = useMemo(() => CATEGORIES.find(c => c.key === active) || CATEGORIES[0], [active]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchSettings()); }
    catch { message.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); fetchPropertyTypes().then(setPropertyTypes).catch(() => {}); }, [load]);

  const rows = data[active] || [];

  const openCreate = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ IsActive: true, SortOrder: (rows.length + 1) * 1, RequiresNext: true }); setModalOpen(true); };
  const openEdit = (rec) => { setEditing(rec); form.setFieldsValue({ ...rec, IsActive: !!rec.IsActive, RequiresNext: !!rec.RequiresNext }); setModalOpen(true); };

  const handleSave = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    const payload = {
      Category: active, Value: v.Value, Label: v.Label,
      Color: meta.color ? toHex(v.Color) : null,
      SortOrder: v.SortOrder || 0,
      IsActive: v.IsActive, RequiresNext: v.RequiresNext,
    };
    try {
      if (editing) await updateSetting(editing.SettingID, payload);
      else await createSetting(payload);
      message.success('Saved');
      setModalOpen(false); load();
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to save');
    }
  };

  const handleDelete = async (rec) => {
    try { await deleteSetting(rec.SettingID); message.success('Deleted'); load(); }
    catch (e) { message.error(e.response?.data?.error || 'Failed to delete'); }
  };

  const toggleActive = async (rec) => {
    try { await updateSetting(rec.SettingID, { IsActive: !rec.IsActive }); load(); }
    catch { message.error('Failed'); }
  };

  const columns = [
    { title: 'Label', dataIndex: 'Label', render: (t, r) => (
      <Space>{meta.color && r.Color ? <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: r.Color }} /> : null}
        <Text>{t}</Text>{r.IsSystem ? <Tooltip title="System default — can't be deleted"><LockOutlined style={{ color: '#bfbfbf' }} /></Tooltip> : null}</Space>
    ) },
    { title: 'Value', dataIndex: 'Value', render: (t) => <Text type="secondary">{t}</Text> },
    { title: 'Order', dataIndex: 'SortOrder', width: 80, align: 'center' },
    ...(meta.requiresNext ? [{ title: 'Needs next', dataIndex: 'RequiresNext', width: 100, align: 'center', render: (v) => v ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag> }] : []),
    { title: 'Active', dataIndex: 'IsActive', width: 90, align: 'center', render: (v, r) => <Switch size="small" checked={!!v} onChange={() => toggleActive(r)} /> },
    { title: 'Actions', key: 'a', width: 110, align: 'center', render: (_, r) => (
      <Space size={0}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
        <Popconfirm title="Delete this option?" okText="Delete" okButtonProps={{ danger: true }} disabled={!!r.IsSystem} onConfirm={() => handleDelete(r)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={!!r.IsSystem} />
        </Popconfirm>
      </Space>
    ) },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Settings</Title>
          <Text type="secondary">Manage dropdown masters used across the CRM</Text>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={6}>
          <Card bordered={false} style={{ borderRadius: 8 }} bodyStyle={{ padding: 8 }}>
            <Menu mode="inline" selectedKeys={[active]} onSelect={({ key }) => setActive(key)}
              style={{ border: 'none' }}
              items={[...CATEGORIES.map(c => ({ key: c.key, label: c.label })),
                { type: 'divider' },
                { key: '__property', label: 'Property Types (fixed)' }]} />
          </Card>
        </Col>
        <Col xs={24} md={18}>
          <Card bordered={false} style={{ borderRadius: 8 }}
            title={active === '__property' ? 'Property Types' : meta.label}
            extra={active !== '__property' ? <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add</Button> : null}>
            {active === '__property' ? (
              <>
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  message="Property types are predefined and not editable." />
                <Space wrap>{propertyTypes.map(p => <Tag key={p} style={{ padding: '4px 10px' }}>{p}</Tag>)}</Space>
              </>
            ) : (
              <Table size="small" bordered rowKey="SettingID" loading={loading} dataSource={rows}
                columns={columns} pagination={rows.length > 12 ? { pageSize: 12 } : false} />
            )}
          </Card>
        </Col>
      </Row>

      <Modal title={editing ? 'Edit Option' : 'Add Option'} open={modalOpen} onCancel={() => setModalOpen(false)}
        onOk={handleSave} okText="Save" destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="Label" label="Label" rules={[{ required: true, message: 'Enter a label' }]}>
            <Input placeholder="Shown to users" onChange={(e) => { if (!editing) form.setFieldValue('Value', e.target.value); }} />
          </Form.Item>
          <Form.Item name="Value" label="Value" rules={[{ required: true, message: 'Enter a value' }]}
            tooltip="Stored value (defaults to the label)">
            <Input placeholder="Stored value" disabled={!!editing} />
          </Form.Item>
          {meta.color && (
            <Form.Item name="Color" label="Color">
              <ColorPicker format="hex" />
            </Form.Item>
          )}
          <Form.Item name="SortOrder" label="Sort Order"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          {meta.requiresNext && (
            <Form.Item name="RequiresNext" label="Requires next follow-up" valuePropName="checked"
              tooltip="If on, choosing this status forces scheduling the next follow-up">
              <Switch />
            </Form.Item>
          )}
          <Form.Item name="IsActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
